/**
 * Button Component
 * Reusable button with active, pressed, and disabled states
 * 
 * @author Varun Pandey
 */

import React from 'react';
import { cn } from '../../utils/cn';
import { Loader } from './Loader';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'active' | 'disabled' | 'secondary' | 'ghost';
  isLoading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'active', isLoading = false, disabled, children, ...props }, ref) => {
    const isDisabled = disabled || isLoading || variant === 'disabled';

    return (
      <button
        ref={ref}
        className={cn(
          'relative inline-flex flex-row items-center justify-center gap-2 rounded-[0.75rem] px-6 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          variant === 'active' && !isDisabled && 'text-white bg-[#F33363] hover:bg-[#D92D58] active:bg-[#D92D58]',
          variant === 'disabled' && 'text-white bg-[#F33363]/30',
          variant === 'secondary' && 'text-white bg-[#3A3A3C] hover:bg-[#48484A] border border-[#3A3A3C]',
          variant === 'ghost' && 'text-[#8E8E93] hover:bg-white/5 hover:text-white',
          isDisabled && variant === 'active' && 'text-white bg-[#F33363]/30',
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {isLoading && (
          <span className="absolute inset-0 left-0 right-0 top-0 bottom-0 flex items-center justify-center">
            <Loader size="small" />
          </span>
        )}
        <span className={cn('inline-flex flex-row items-center gap-2', isLoading && 'opacity-0')}>{children}</span>
      </button>
    );
  }
);

Button.displayName = 'Button';
